export type NameTranslations = Record<string, { name?: string }>;

export function resolveName(
  translations: NameTranslations | null | undefined,
  lang: string,
  fallback: string | null | undefined,
): string {
  return translations?.[lang]?.name || fallback || "";
}

/**
 * Offered secondary languages that have no name translation — visitors in those
 * languages fall back to the primary name. Used to flag incomplete i18n.
 */
export function missingNameLangs(
  translations: NameTranslations | null | undefined,
  offeredLangs: string[],
  primary: string,
): string[] {
  return offeredLangs.filter(
    (c) => c !== primary && !translations?.[c]?.name?.trim(),
  );
}

/** Per-language title (+ optional description) bag — hotspots, and any
 *  translatable content that carries both fields. */
export type ContentTranslations = Record<
  string,
  { title?: string; description?: string }
>;

/**
 * Offered secondary languages whose translation is missing a field the primary
 * language HAS — i.e. visitors in those languages would fall back. Mirrors
 * `missingNameLangs` but for `{title, description}` content (hotspots). A lang is
 * flagged when: the primary has a title and the lang lacks it, or (when
 * `needDescription`) the primary has a description and the lang lacks it.
 * `base` folds in the legacy flat title/description columns.
 */
export function missingContentLangs(
  translations: ContentTranslations | null | undefined,
  offeredLangs: string[],
  primary: string,
  base: { title?: string | null; description?: string | null },
  needDescription: boolean,
): string[] {
  const primaryTitle = (translations?.[primary]?.title || base.title || "").trim();
  const primaryDesc = (
    translations?.[primary]?.description ||
    base.description ||
    ""
  ).trim();
  return offeredLangs.filter((c) => {
    if (c === primary) return false;
    const entry = translations?.[c];
    const missingTitle = !!primaryTitle && !entry?.title?.trim();
    const missingDesc =
      needDescription && !!primaryDesc && !entry?.description?.trim();
    return missingTitle || missingDesc;
  });
}
